import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import useSubscriptions from '../../../store/models/subscriptions/useSubscriptions';
import useSubscriptionDialogs from './useSubscriptionDialogs.jsx';
import SubscriptionCardContainer from './subscriptionCardContainer';
import InactiveSubscriptionCardContainer from './inactiveSubscriptionCardContainer';
import { getSubscriptionAnchorId } from '../../components/subscriptionCard/anchorId';
import * as styles from './styles';

const sectionLabel = {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--ecal-lightgrey)',
    mb: 1.5,
};

function SubscriptionsPanel({ focusSubscriptionId, onFocusHandled }) {
    const {
        subscriptions,
        inactiveSubscriptions,
        subscriptionDetails,
        pageInfo,
        isLoadingMore,
        hasMoreSubscriptions,
        unsubscribe,
        removeSchedule,
        addSchedule,
        loadAvailableSchedules,
        loadMoreSubscriptions,
    } = useSubscriptions();

    const {
        openUnsubscribeDialog,
        openRemoveDialog,
        handleAddSchedule,
        renderDialogs,
    } = useSubscriptionDialogs({ unsubscribe, addSchedule, removeSchedule });

    useEffect(() => {
        subscriptions.forEach((sub) => {
            loadAvailableSchedules(sub.id);
        });
    }, [subscriptions]);

    useEffect(() => {
        if (!focusSubscriptionId) return;

        const target = document.getElementById(getSubscriptionAnchorId(focusSubscriptionId));
        if (!target) {
            if (subscriptions.length) onFocusHandled();
            return;
        }

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
        onFocusHandled();
    }, [focusSubscriptionId, subscriptions]);

    function handleUnsubscribeClick(subscription) {
        const publisher = subscriptionDetails[subscription.id]?.publisher || subscription.publisher;
        openUnsubscribeDialog({ ...subscription, publisher });
    }

    function renderCard(subscription) {
        return (
            <SubscriptionCardContainer
                key={subscription.id}
                subscription={subscription}
                onUnsubscribe={() => handleUnsubscribeClick(subscription)}
                onRemoveSchedule={openRemoveDialog}
                onAddSchedule={handleAddSchedule}
            />
        );
    }

    function renderInactiveCard(subscription) {
        return (
            <InactiveSubscriptionCardContainer
                key={subscription.id}
                subscription={subscription}
            />
        );
    }

    function renderHeader() {
        return (
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--ecal-lightblue)', mb: 0.5 }}>
                    Manage My ECAL
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography variant="h2" sx={{ fontWeight: 600, color: 'var(--ecal-white)', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        My subscriptions
                        {subscriptions.length > 0 && (
                            <>
                                {' · '}
                                <Box component="span" sx={{ background: 'linear-gradient(90deg, #00D7FF, #0078FF)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                                    {subscriptions.length}
                                </Box>
                            </>
                        )}
                    </Typography>
                    {renderLoadMoreBar()}
                </Box>
                <Typography sx={{ color: 'var(--ecal-lightgrey)', mt: 0.5, fontSize: '0.875rem' }}>
                    Curate your ultimate schedule in one place. Add or remove schedules and brands, discover personalised recommendations and visualise your collection. Updates sync to your calendar instantly.
                </Typography>
            </Box>
        );
    }

    // Hidden entirely once nothing's left to page in.
    function renderLoadMoreBar() {
        if (!pageInfo.totalRecords) return null;
        return (
            <Box sx={styles.loadMoreBar}>
                <Typography sx={styles.loadMoreText}>
                    {pageInfo.totalPages > 1
                        ? `Subscriptions found: ${pageInfo.totalRecords} (${pageInfo.totalLoaded} loaded)`
                        : `Subscriptions found: ${pageInfo.totalRecords}`}
                </Typography>
                {hasMoreSubscriptions && (
                    <Box
                        component="button"
                        disabled={isLoadingMore}
                        onClick={loadMoreSubscriptions}
                        sx={styles.loadMoreButton}
                    >
                        {isLoadingMore ? 'Loading…' : 'Load more'}
                    </Box>
                )}
            </Box>
        );
    }

    function renderEmpty() {
        return (
            <Box sx={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)',
                border: '1px solid rgba(187,188,188,0.12)',
                borderRadius: '22px',
                py: 8,
                textAlign: 'center',
                color: 'var(--ecal-lightgrey)',
            }}>
                <Typography>You don't have any active subscriptions yet.</Typography>
                <Typography sx={styles.discoveryLinkText}>
                    Browse the{' '}
                    <Box component={Link} to="/#directory" sx={styles.discoveryLink}>
                        discovery portal
                    </Box>
                    {' '}or your recommendations to add one.
                </Typography>
            </Box>
        );
    }

    function renderActiveList() {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {subscriptions.map(renderCard)}
            </Box>
        );
    }

    function renderInactiveList() {
        if (!inactiveSubscriptions.length) return null;
        return (
            <Box sx={{ mt: 4 }}>
                <Typography sx={sectionLabel}>Previously synced</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {inactiveSubscriptions.map(renderInactiveCard)}
                </Box>
            </Box>
        );
    }

    function renderBody() {
        return (
            <>
                {renderHeader()}
                {subscriptions.length === 0 ? renderEmpty() : renderActiveList()}
                {renderInactiveList()}
            </>
        );
    }

    return (
        <Box>
            {renderBody()}
            {renderDialogs()}
        </Box>
    );
}

export default observer(SubscriptionsPanel);
